import { Fragment } from "react";
import { Check, ChevronsUpDown, Plus, Trash2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import { Label } from "@/shared/components/ui/label";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { GfsOfferHtmlEditor } from "@/services/backend/gfs/offer/components/offer-html-editor";
import {
  usePestcontrolPestGroupListQuery,
  usePestcontrolPestListQuery,
} from "@/services/backend/pestcontrol/api/pest-api";
import { PestKind } from "@/services/backend/pestcontrol/types/pest";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { cn } from "@/shared/lib/utils";
import { TextInputField } from "@/shared/components/field/input-field";
import {
  GfsOfferConfig,
  GfsCeoGender,
  GfsOfferTextBlock,
} from "@/services/backend/gfs/offer/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

type Props = {
  config: GfsOfferConfig;
  onChange: (config: GfsOfferConfig) => void;
  disabled?: boolean;
  mode?: "baseData" | "offer";
};

export function GfsOfferConfigEditor({
  config,
  onChange,
  disabled,
  mode = "baseData",
}: Props) {
  if (mode === "offer") {
    return (
      <OfferTextBlockEditor
        config={config}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }

  const rows = sortOfferConfigRows(config.rows ?? []);
  const updateRows = (nextRows: GfsOfferConfig["rows"]) =>
    onChange({ ...config, rows: nextRows });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Geschäftsführer</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <TextInputField
            label="Name"
            text={config.ceoName ?? ""}
            disabled={disabled}
            onTextChange={(ceoName) => onChange({ ...config, ceoName })}
          />
          <div className="grid gap-1.5">
            <Label htmlFor="gfs-ceo-gender">Bezeichnung</Label>
            <Select
              value={config.ceoGender ?? ""}
              onValueChange={(value) =>
                onChange({
                  ...config,
                  ceoGender: value as GfsCeoGender,
                })
              }
              disabled={disabled}
            >
              <SelectTrigger id="gfs-ceo-gender">
                <SelectValue placeholder="m / w / d" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="m">m</SelectItem>
                <SelectItem value="w">w</SelectItem>
                <SelectItem value="d">d</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      <PreparedSection
        title="Anschreiben"
        blocks={config.coverLetter ?? []}
        disabled={disabled}
        onChange={(coverLetter) => onChange({ ...config, coverLetter })}
      />
      {rows
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => reusableSectionKeys.has(row.key))
        .map(({ row, index }) => (
          <PreparedSection
            key={row.key}
            title={row.label}
            blocks={row.blocks ?? []}
            defaultDisplay={row.visible}
            disabled={disabled}
            onDefaultDisplayChange={(visible) =>
              updateRows(
                rows.map((candidate, candidateIndex) =>
                  candidateIndex === index
                    ? { ...candidate, visible }
                    : candidate,
                ),
              )
            }
            onChange={(blocks) =>
              updateRows(
                rows.map((candidate, candidateIndex) =>
                  candidateIndex === index
                    ? { ...candidate, blocks }
                    : candidate,
                ),
              )
            }
          />
        ))}
      <PreparedSection
        title="Abschlusstext"
        blocks={config.closingText ?? []}
        disabled={disabled}
        onChange={(closingText) => onChange({ ...config, closingText })}
      />
    </div>
  );
}

function OfferTextBlockEditor({ config, disabled, onChange }: Props) {
  const activeRows = sortOfferConfigRows(config.rows ?? [])
    .map((row, index) => ({ row, index }))
    .filter(
      ({ row }) =>
        offerTextSectionKeys.has(row.key) && hasSelectedBlock(row.blocks ?? []),
    );
  const updateRows = (rows: GfsOfferConfig["rows"]) =>
    onChange({ ...config, rows });

  const updateSectionBlocks = (
    sectionKey: string,
    blocks: GfsOfferTextBlock[],
  ) => {
    updateRows(
      (config.rows ?? []).map((row) =>
        row.key === sectionKey ? { ...row, visible: true, blocks } : row,
      ),
    );
  };

  const addCustomBlock = (sectionKey: string) => {
    const section = getSectionBlocks(config, sectionKey);
    updateSectionBlocks(sectionKey, [
      ...section.blocks,
      {
        id: `custom-${crypto.randomUUID()}`,
        title: "Neuer Textblock",
        body: "<p></p>",
        selected: true,
        position: "after",
      },
    ]);
  };

  const addLibraryBlock = (sectionKey: string, blockId: string) => {
    const section = getSectionBlocks(config, sectionKey);
    updateSectionBlocks(
      sectionKey,
      section.blocks.map((block) =>
        block.id === blockId ? { ...block, selected: true } : block,
      ),
    );
  };

  const sections = [
    ...activeRows.map(({ row }) => ({
      key: row.key,
      title: row.label,
      blocks: row.blocks ?? [],
    })),
  ];

  return (
    <div className="space-y-6">
      {sections.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8">
          <GfsOfferTextAddButton
            config={config}
            disabled={disabled}
            onAddLibraryBlock={addLibraryBlock}
            onAddCustomBlock={(sectionKey) => {
              if (sectionKey) addCustomBlock(sectionKey);
            }}
          />
          <p className="text-muted-foreground text-sm">
            Noch keine Textblöcke ausgewählt.
          </p>
        </div>
      ) : (
        sections.map((section) => (
          <OfferTextSection
            key={section.key}
            title={section.title}
            blocks={section.blocks}
            disabled={disabled}
            onAddLibraryBlock={(blockId) =>
              addLibraryBlock(section.key, blockId)
            }
            onAddCustomBlock={() => addCustomBlock(section.key)}
            onChange={(blocks) => updateSectionBlocks(section.key, blocks)}
          />
        ))
      )}
    </div>
  );
}

export function GfsOfferTextBlocksEditor({
  title,
  blocks,
  libraryBlocks,
  disabled,
  onChange,
}: {
  title: string;
  blocks: GfsOfferTextBlock[];
  libraryBlocks: GfsOfferTextBlock[];
  disabled?: boolean;
  onChange: (blocks: GfsOfferTextBlock[]) => void;
}) {
  const mergedBlocks = mergeOfferTextBlocks(blocks, libraryBlocks);

  return (
    <OfferTextSection
      title={title}
      blocks={mergedBlocks}
      disabled={disabled}
      onAddLibraryBlock={(blockId) =>
        onChange(
          mergedBlocks.map((block) =>
            block.id === blockId ? { ...block, selected: true } : block,
          ),
        )
      }
      onAddCustomBlock={() =>
        onChange([
          ...mergedBlocks,
          {
            id: `custom-${crypto.randomUUID()}`,
            title: "Neuer Textblock",
            body: "<p></p>",
            selected: true,
            position: "after",
          },
        ])
      }
      onChange={onChange}
    />
  );
}

function OfferTextSection({
  title,
  blocks,
  disabled,
  onAddLibraryBlock,
  onAddCustomBlock,
  onChange,
}: {
  title: string;
  blocks: GfsOfferTextBlock[];
  disabled?: boolean;
  onAddLibraryBlock: (blockId: string) => void;
  onAddCustomBlock: () => void;
  onChange: (blocks: GfsOfferTextBlock[]) => void;
}) {
  const selectedBlocks = blocks.filter((block) => block.selected);

  return (
    <section className="space-y-4 border-b pb-6 last:border-b-0">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="flex flex-col">
        <div className="flex justify-center py-1">
          <GfsOfferTextAddButton
            blocks={blocks}
            disabled={disabled}
            onAddLibraryBlock={(_, blockId) => onAddLibraryBlock(blockId)}
            onAddCustomBlock={onAddCustomBlock}
            sectionTitle={title}
          />
        </div>
        {selectedBlocks.map((block) => (
          <Fragment key={block.id}>
            <OfferTextBlock
              block={block}
              disabled={disabled}
              onChange={(next) =>
                onChange(
                  blocks.map((candidate) =>
                    candidate.id === block.id ? next : candidate,
                  ),
                )
              }
              onRemove={() =>
                onChange(
                  block.id.startsWith("custom-")
                    ? blocks.filter((candidate) => candidate.id !== block.id)
                    : blocks.map((candidate) =>
                        candidate.id === block.id
                          ? { ...candidate, selected: false }
                          : candidate,
                      ),
                )
              }
            />
            <div className="flex justify-center py-1">
              <GfsOfferTextAddButton
                blocks={blocks}
                disabled={disabled}
                onAddLibraryBlock={(_, blockId) => onAddLibraryBlock(blockId)}
                onAddCustomBlock={onAddCustomBlock}
                sectionTitle={title}
              />
            </div>
          </Fragment>
        ))}
      </div>
    </section>
  );
}

export function GfsOfferTextAddButton({
  config,
  blocks,
  disabled,
  onAddLibraryBlock,
  onAddCustomBlock,
  sectionTitle,
}: {
  config?: GfsOfferConfig;
  blocks?: GfsOfferTextBlock[];
  disabled?: boolean;
  onAddLibraryBlock: (sectionKey: string, blockId: string) => void;
  onAddCustomBlock: (sectionKey?: string) => void;
  sectionTitle?: string;
}) {
  const libraryEntries = config
    ? getOfferTextSections(config).flatMap((section) =>
        section.blocks
          .filter((block) => !block.selected)
          .map((block) => ({
            sectionKey: section.key,
            sectionTitle: section.title,
            block,
          })),
      )
    : (blocks ?? [])
        .filter((block) => !block.selected)
        .map((block) => ({
          sectionKey: "",
          sectionTitle: sectionTitle ?? "",
          block,
        }));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="text-muted-foreground h-6 w-6"
          disabled={disabled}
          aria-label="Textblock hinzufügen"
          title="Textblock hinzufügen"
        >
          <Plus className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(28rem,calc(100vw-2rem))] p-0"
        align="end"
      >
        <Command>
          <CommandInput placeholder="Textblock suchen..." />
          <CommandList>
            <CommandEmpty>Keine weiteren Textblöcke</CommandEmpty>
            {config && (
              <CommandGroup heading="Individueller Textblock">
                {getOfferTextSections(config).map((section) => (
                  <CommandItem
                    key={`custom-${section.key}`}
                    value={`individueller textblock ${section.title}`}
                    onSelect={() => onAddCustomBlock(section.key)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {section.title}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {libraryEntries.length > 0 && (
              <CommandGroup heading="Bibliotheksblöcke">
                {libraryEntries.map(
                  ({ sectionKey, sectionTitle: entryTitle, block }) => (
                    <CommandItem
                      key={`${sectionKey}-${block.id}`}
                      value={`${entryTitle} ${block.title}`}
                      onSelect={() => onAddLibraryBlock(sectionKey, block.id)}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      <span className="truncate">
                        {entryTitle}: {block.title || "Ohne Titel"}
                      </span>
                    </CommandItem>
                  ),
                )}
              </CommandGroup>
            )}
            {!config && (
              <CommandGroup heading="Individueller Textblock">
                <CommandItem
                  value="individueller textblock"
                  onSelect={onAddCustomBlock}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Textblock anlegen
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function OfferTextBlock({
  block,
  disabled,
  onChange,
  onRemove,
}: {
  block: GfsOfferTextBlock;
  disabled?: boolean;
  onChange: (block: GfsOfferTextBlock) => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-3 border-l-2 pl-4">
      <div className="flex items-center justify-between gap-3">
        <InputDebounced
          value={block.title}
          disabled={disabled}
          aria-label="Textblock-Titel"
          onChange={(event) =>
            onChange({ ...block, title: event.target.value })
          }
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          disabled={disabled}
          onClick={onRemove}
          aria-label="Textblock entfernen"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <GfsOfferHtmlEditor
        value={block.body}
        disabled={disabled}
        onChange={(body) => onChange({ ...block, body })}
      />
      <details className="rounded-md border px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium">
          Bedingungen
        </summary>
        <div className="pt-3">
          <TextBlockConditionEditor
            block={block}
            disabled={disabled}
            compact
            onChange={onChange}
          />
        </div>
      </details>
    </div>
  );
}

function getSectionBlocks(config: GfsOfferConfig, sectionKey: string) {
  if (sectionKey === "coverLetter") {
    return {
      key: sectionKey,
      title: "Anschreiben",
      blocks: config.coverLetter ?? [],
    };
  }
  if (sectionKey === "closingText") {
    return {
      key: sectionKey,
      title: "Abschlusstext",
      blocks: config.closingText ?? [],
    };
  }
  const row = config.rows.find((candidate) => candidate.key === sectionKey);
  return row
    ? { key: row.key, title: row.label, blocks: row.blocks ?? [] }
    : { key: sectionKey, title: sectionKey, blocks: [] };
}

function getOfferTextSections(config: GfsOfferConfig) {
  return [
    ...sortOfferConfigRows(config.rows ?? [])
      .filter((row) => offerTextSectionKeys.has(row.key))
      .map((row) => ({
        key: row.key,
        title: row.label,
        blocks: row.blocks ?? [],
      })),
  ];
}

function mergeOfferTextBlocks(
  blocks: GfsOfferTextBlock[],
  libraryBlocks: GfsOfferTextBlock[],
) {
  const existingIds = new Set(blocks.map((block) => block.id));
  return [
    ...blocks,
    ...libraryBlocks
      .filter((block) => !existingIds.has(block.id))
      .map((block) => ({ ...block, selected: false })),
  ];
}

function hasSelectedBlock(blocks: GfsOfferTextBlock[]) {
  return blocks.some((block) => block.selected);
}

const reusableSectionKeys = new Set([
  "services",
  "investment",
  "customerPreparation",
  "customerSupport",
  "products",
  "warranty",
  "importantNotes",
  "agreement",
]);

const offerTextSectionKeys = new Set([
  "situation",
  "services",
  "investment",
  "timeWindow",
  "appointment",
  "customerPreparation",
  "customerSupport",
  "products",
  "warranty",
  "importantNotes",
  "agreement",
]);

function sortOfferConfigRows(rows: GfsOfferConfig["rows"]) {
  return rows
    .map((row, index) => ({ row, index }))
    .sort(
      (left, right) =>
        left.row.order - right.row.order || left.index - right.index,
    )
    .map(({ row }) => row);
}

function PreparedSection({
  title,
  blocks,
  defaultDisplay,
  onDefaultDisplayChange,
  onChange,
  disabled,
}: {
  title: string;
  blocks: GfsOfferTextBlock[];
  defaultDisplay?: boolean;
  onDefaultDisplayChange?: (value: boolean) => void;
  onChange: (blocks: GfsOfferTextBlock[]) => void;
  disabled?: boolean;
}) {
  const addTextBlock = () => {
    onChange([
      ...blocks,
      {
        id: crypto.randomUUID(),
        title: "Neuer Textblock",
        body: "<p></p>",
        selected: true,
        position: "after",
      },
    ]);
  };
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className="flex flex-row items-center justify-between border-b px-4 py-3 [.border-b]:pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <PersistenceStateUsingContext className="shrink-0" />
      </CardHeader>
      <CardContent className="space-y-3 p-3">
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={addTextBlock}
          >
            <Plus className="mr-1 h-4 w-4" />
            Textblock
          </Button>
        </div>
        {onDefaultDisplayChange ? (
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={defaultDisplay ?? true}
              disabled={disabled}
              onCheckedChange={(value) =>
                onDefaultDisplayChange(Boolean(value))
              }
            />
            <span>Standardmäßig anzeigen</span>
          </div>
        ) : null}
        {blocks.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Für diesen Bereich ist noch kein Textblock hinterlegt.
          </p>
        ) : null}
        <div className="space-y-3">
          {blocks.map((block, index) => (
            <TextBlockCard
              key={block.id}
              block={block}
              disabled={disabled}
              onChange={(next) =>
                onChange(
                  blocks.map((candidate, candidateIndex) =>
                    candidateIndex === index ? next : candidate,
                  ),
                )
              }
              onDelete={() =>
                onChange(blocks.filter((_, itemIndex) => itemIndex !== index))
              }
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TextBlockCard({
  block,
  onChange,
  onDelete,
  disabled,
}: {
  block: GfsOfferTextBlock;
  onChange: (block: GfsOfferTextBlock) => void;
  onDelete: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium">
            {block.title || "Ohne Titel"}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            disabled={disabled}
            onClick={onDelete}
            aria-label="Textblock löschen"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="space-y-3 p-3">
        <div className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={block.selected}
            disabled={disabled}
            onCheckedChange={(selected) =>
              onChange({ ...block, selected: Boolean(selected) })
            }
          />
          <span>Standardmäßig anzeigen</span>
        </div>
        <TextBlockConditionEditor
          block={block}
          disabled={disabled}
          onChange={onChange}
        />
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Titel</Label>
            <InputDebounced
              value={block.title}
              disabled={disabled}
              onChange={(event) =>
                onChange({ ...block, title: event.target.value })
              }
            />
          </div>
          <GfsOfferHtmlEditor
            value={block.body}
            disabled={disabled}
            onChange={(body) => onChange({ ...block, body })}
          />
        </div>
      </div>
    </div>
  );
}

type ConditionOption = { id: string; label: string };

function TextBlockConditionEditor({
  block,
  disabled,
  compact = false,
  onChange,
}: {
  block: GfsOfferTextBlock;
  disabled?: boolean;
  compact?: boolean;
  onChange: (block: GfsOfferTextBlock) => void;
}) {
  const { companyId } = useActiveCompany();
  const { data: groups } = usePestcontrolPestGroupListQuery(
    { company: { active: true, values: [companyId] }, limit: 500, offset: 0 },
    { skip: !companyId },
  );
  const { data: pests } = usePestcontrolPestListQuery(
    { company: { active: true, values: [companyId] }, limit: 500, offset: 0 },
    { skip: !companyId },
  );
  if (!companyId) return null;
  const groupOptions = (groups?.items ?? []).map((group) => ({
    id: group.id,
    label: group.name,
  }));
  const categoryOptions = (pests?.items ?? [])
    .filter((pest) => pest.kind === PestKind.Category)
    .map((pest) => ({ id: pest.id, label: pest.name }));
  const indicatorOptions = (pests?.items ?? [])
    .filter((pest) => pest.kind === PestKind.Indicator)
    .map((pest) => ({ id: pest.id, label: pest.name }));
  return (
    <div
      className={cn(
        "bg-muted/20 space-y-2 rounded-md p-2",
        compact && "bg-transparent p-0",
      )}
    >
      {!compact && (
        <p className="text-muted-foreground text-xs">
          Optional: Textblock nur bei passendem Schädling anzeigen
        </p>
      )}
      <div className="grid gap-2 md:grid-cols-3">
        <ConditionPicker
          title="Schädlingsgruppen"
          options={groupOptions}
          selectedIds={block.pestGroupIds ?? []}
          disabled={disabled}
          onChange={(pestGroupIds) => onChange({ ...block, pestGroupIds })}
        />
        <ConditionPicker
          title="Schädlingskategorien"
          options={categoryOptions}
          selectedIds={block.pestCategoryIds ?? []}
          disabled={disabled}
          onChange={(pestCategoryIds) =>
            onChange({ ...block, pestCategoryIds })
          }
        />
        <ConditionPicker
          title="Indikatoren"
          options={indicatorOptions}
          selectedIds={block.pestIndicatorIds ?? []}
          disabled={disabled}
          onChange={(pestIndicatorIds) =>
            onChange({ ...block, pestIndicatorIds })
          }
        />
      </div>
    </div>
  );
}

function ConditionPicker({
  title,
  options,
  selectedIds,
  disabled,
  onChange,
}: {
  title: string;
  options: ConditionOption[];
  selectedIds: string[];
  disabled?: boolean;
  onChange: (ids: string[]) => void;
}) {
  const selected = new Set(selectedIds);
  const selectedLabel =
    selectedIds.length === 0
      ? "Alle Schädlingstypen"
      : selectedIds.length === 1
        ? (options.find((option) => option.id === selectedIds[0])?.label ??
          selectedIds[0])
        : `${selectedIds.length} ausgewählt`;
  return (
    <div className="space-y-1">
      <Label className="text-xs">{title}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">{selectedLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Suchen" />
            <CommandList>
              <CommandEmpty>Keine Treffer</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={`${option.id} ${option.label}`}
                    onSelect={() => {
                      const next = selected.has(option.id)
                        ? selectedIds.filter((id) => id !== option.id)
                        : [...selectedIds, option.id];
                      onChange(next);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selected.has(option.id) ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
